import {
  challengeData,
  durationOptions,
  formatTimeValue,
  objectiveTypes,
} from "@/components/missions/config";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useActiveClub } from "../../data/clubs/hooks";
import { useCreateMission } from "../../data/missions/hooks";
import { MissionObjectiveType, MissionType } from "../../data/missions/types";
import { useClubTeams } from "../../data/teams/hooks";

export const Route = createFileRoute("/missions/create")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const activeClub = useActiveClub();
  const { data: clubTeams } = useClubTeams();
  const createMissionMutation = useCreateMission();

  const totalMembers = activeClub?.totalMembers || 1;

  const nonEmptyTeams = clubTeams?.filter((team) => team.members.length > 0);
  const averageMembersPerTeam =
    (nonEmptyTeams?.reduce((acc, team) => acc + team.members.length, 0) || 0) /
    (nonEmptyTeams?.length || 1);

  const [missionType, setMissionType] = useState<MissionType>("CLUB");
  const [intensity, setIntensity] = useState<
    "LIGHT" | "MEDIUM" | "INTENSE" | "CUSTOM"
  >("LIGHT");
  const [objectiveType, setObjectiveType] =
    useState<MissionObjectiveType>("CALORIES_BURNED");
  const [duration, setDuration] = useState("1w");
  const [name, setName] = useState("");
  const [customValue, setCustomValue] = useState<number>(0);
  const [description, setDescription] = useState("");
  const [userEditedDescription, setUserEditedDescription] = useState(false);
  const [totalParticipants, setTotalParticipants] = useState(
    missionType === "CLUB" ? totalMembers : averageMembersPerTeam
  );

  // Update total participants based on selection
  useEffect(() => {
    setTotalParticipants(
      missionType === "CLUB" ? totalMembers : averageMembersPerTeam
    );
  }, [missionType, activeClub, clubTeams]);

  // Generate auto description
  useEffect(() => {
    if (userEditedDescription) return;

    const durationLabel =
      durationOptions.find((d) => d.value === duration)?.label || "1 Week";
    const unitLabel =
      objectiveType === "CALORIES_BURNED"
        ? "calories"
        : objectiveType === "ACTIVE_MINUTES"
          ? ""
          : "km";

    let totalValue = 0;

    if (intensity === "CUSTOM") {
      totalValue = customValue * 60;
    } else {
      const selectedChallenge = challengeData.find(
        (challenge) =>
          challenge.intensity === intensity &&
          challenge.objectiveType === objectiveType
      );
      const weeklyValue = selectedChallenge?.value || 0;
      const durationWeeks =
        durationOptions.find((d) => d.value === duration)?.weeks || 1;
      totalValue = weeklyValue * durationWeeks * totalParticipants;
    }

    // Format the value for display
    const formattedValue =
      objectiveType === "ACTIVE_MINUTES"
        ? formatTimeValue(totalValue)
        : totalValue.toLocaleString();

    // Get the verb based on objective type
    let verb = "Complete";
    if (objectiveType === "CALORIES_BURNED") verb = "Burn";
    else if (objectiveType.startsWith("DISTANCE"))
      verb = objectiveType.includes("SWIM")
        ? "Swim"
        : objectiveType.includes("BIKE")
          ? "Cycle"
          : objectiveType.includes("RUN")
            ? "Run"
            : "Walk";

    const newAutoDescription = `${verb} ${formattedValue}${unitLabel ? ` ${unitLabel}` : ""} as a ${missionType.toLowerCase()} in ${durationLabel.toLowerCase()}.`;

    setDescription(newAutoDescription);
  }, [
    missionType,
    intensity,
    objectiveType,
    duration,
    customValue,
    totalParticipants,
    userEditedDescription,
  ]);

  // Find the matching challenge and update name
  useEffect(() => {
    if (intensity !== "CUSTOM") {
      const selectedChallenge = challengeData.find(
        (challenge) =>
          challenge.intensity === intensity &&
          challenge.objectiveType === objectiveType
      );

      if (selectedChallenge) {
        setName(selectedChallenge.name);
      }
    } else {
      setName("Custom Challenge");
    }
  }, [intensity, objectiveType]);

  // Get intensity value for the selected objective type
  const getIntensityValue = (intensityLevel: string) => {
    const challenge = challengeData.find(
      (c) => c.intensity === intensityLevel && c.objectiveType === objectiveType
    );
    if (!challenge) return 0;

    const durationWeeks =
      durationOptions.find((d) => d.value === duration)?.weeks || 1;
    return challenge.value * durationWeeks * totalParticipants;
  };

  // Get unit label based on objective type
  const getUnitLabel = () => {
    if (objectiveType === "CALORIES_BURNED") return "calories";
    if (objectiveType === "ACTIVE_MINUTES") return "minutes";
    return "km";
  };

  // Format intensity value with appropriate unit
  const formatIntensityValue = (intensityLevel: string) => {
    const value = getIntensityValue(intensityLevel);

    // Format based on objective type
    if (objectiveType === "ACTIVE_MINUTES") {
      return formatTimeValue(value);
    } else if (objectiveType === "CALORIES_BURNED") {
      return `${value.toLocaleString()} calories`;
    } else {
      // Distance types
      return `${value.toLocaleString()} km`;
    }
  };

  // Handle mission creation
  const handleCreateMission = () => {
    if (!activeClub?.id) {
      console.error("No active club selected");
      return;
    }

    // Calculate objective value based on intensity
    const objectiveValue =
      intensity === "CUSTOM" ? customValue * 60 : getIntensityValue(intensity);

    // Prepare mission data
    const missionData = {
      title: name,
      description: description,
      type: missionType,
      objectiveType: objectiveType,
      objectiveValue: objectiveValue,
      clubId: activeClub.id,
      durationInWeeks:
        durationOptions.find((d) => d.value === duration)?.weeks || 1,
    };

    // Submit the mutation
    createMissionMutation.mutate(missionData, {
      onSuccess: () => {
        navigate({ to: "/missions" });
      },
      onError: (error) => {
        console.error("Failed to create mission:", error);
        // Here you could add toast notifications or other error handling
      },
    });
  };

  return (
    <div className="w-full h-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
            Create Mission
          </h1>
          <p className="text-gray-600">
            Set up a new mission for your club or team
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Step 1: Select Participant Type */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold flex items-center gap-2 text-[#2b7fff]">
              <span>Step 1: Select Participant Type</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setMissionType("CLUB")}
                className={`cursor-pointer p-4 rounded-lg border-2 ${
                  missionType === "CLUB"
                    ? "border-[#2b7fff] bg-blue-50"
                    : "border-gray-200 hover:border-blue-200"
                }`}
              >
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <div className="text-2xl mr-2">👥</div>
                    <h4 className="font-medium">Club</h4>
                  </div>
                  <div className="mt-1">
                    <p className="text-sm text-gray-500">
                      All club members working towards a shared goal
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {totalMembers} members total
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={
                  !clubTeams?.length || averageMembersPerTeam <= 0
                    ? {}
                    : { scale: 1.02 }
                }
                whileTap={
                  !clubTeams?.length || averageMembersPerTeam <= 0
                    ? {}
                    : { scale: 0.98 }
                }
                onClick={() => {
                  if (clubTeams?.length && averageMembersPerTeam > 0) {
                    setMissionType("TEAM");
                  }
                }}
                className={`p-4 rounded-lg border-2 ${
                  !clubTeams?.length || averageMembersPerTeam <= 0
                    ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                    : missionType === "TEAM"
                      ? "border-[#fe9a00] bg-orange-50 cursor-pointer"
                      : "border-gray-200 hover:border-orange-200 cursor-pointer"
                }`}
              >
                <div className="flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-2xl mr-2">🤝</div>
                      <h4 className="font-medium">Team</h4>
                    </div>
                    {(!clubTeams?.length || averageMembersPerTeam <= 0) && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-gray-100 text-gray-500"
                      >
                        Unavailable
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1">
                    <p className="text-sm text-gray-500">
                      Team members working together towards a challenge
                    </p>
                    {!clubTeams?.length ? (
                      <p className="text-xs text-gray-400 mt-1">
                        No teams available in this club
                      </p>
                    ) : averageMembersPerTeam <= 0 ? (
                      <p className="text-xs text-gray-400 mt-1">
                        Teams have no members
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">
                        Average of {Math.round(averageMembersPerTeam)} members
                        per team
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Step 2: Challenge Parameters */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold flex items-center gap-2 text-[#2b7fff]">
              <span>Step 2: Mission Parameters</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Objective Type Selection */}
              <div className="space-y-2">
                <Label htmlFor="objectiveType">Objective Type</Label>
                <Select
                  value={objectiveType}
                  onValueChange={(value) =>
                    setObjectiveType(value as MissionObjectiveType)
                  }
                >
                  <SelectTrigger id="objectiveType" className="w-full">
                    <SelectValue placeholder="Select objective" />
                  </SelectTrigger>
                  <SelectContent>
                    {objectiveTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <span>{type.emoji}</span>
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration Selection - Moved next to Objective Type */}
              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger id="duration" className="w-full">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <span>📅</span>
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Intensity Selection as Cards - Always 2x2 grid */}
            <div>
              <Label className="block mb-2">Intensity Level</Label>
              <div className="grid grid-cols-2 gap-4">
                {/* Light Intensity Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIntensity("LIGHT")}
                  className={`cursor-pointer p-4 rounded-lg border-2 ${
                    intensity === "LIGHT"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-green-200"
                  }`}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-green-100 text-green-800">
                        Light
                      </Badge>
                      {intensity === "LIGHT" && (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-white"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      Beginner friendly
                    </p>
                    <div className="mt-auto">
                      <p className="text-sm font-medium">
                        Total goal: {formatIntensityValue("LIGHT")}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Medium Intensity Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIntensity("MEDIUM")}
                  className={`cursor-pointer p-4 rounded-lg border-2 ${
                    intensity === "MEDIUM"
                      ? "border-[#fe9a00] bg-orange-50"
                      : "border-gray-200 hover:border-orange-200"
                  }`}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-[#fe9a00]/20 text-[#fe9a00]">
                        Medium
                      </Badge>
                      {intensity === "MEDIUM" && (
                        <div className="w-5 h-5 rounded-full bg-[#fe9a00] flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-white"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      Moderate effort
                    </p>
                    <div className="mt-auto">
                      <p className="text-sm font-medium">
                        Total goal: {formatIntensityValue("MEDIUM")}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Intense Intensity Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIntensity("INTENSE")}
                  className={`cursor-pointer p-4 rounded-lg border-2 ${
                    intensity === "INTENSE"
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 hover:border-red-200"
                  }`}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-red-100 text-red-800">Intense</Badge>
                      {intensity === "INTENSE" && (
                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-white"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-2">Advanced level</p>
                    <div className="mt-auto">
                      <p className="text-sm font-medium">
                        Total goal: {formatIntensityValue("INTENSE")}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Custom Intensity Card - Changed to blue */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIntensity("CUSTOM")}
                  className={`cursor-pointer p-4 rounded-lg border-2 ${
                    intensity === "CUSTOM"
                      ? "border-[#2b7fff] bg-blue-50"
                      : "border-gray-200 hover:border-blue-200"
                  }`}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-blue-100 text-[#2b7fff]">
                        Custom
                      </Badge>
                      {intensity === "CUSTOM" && (
                        <div className="w-5 h-5 rounded-full bg-[#2b7fff] flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-white"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      Set your own goal
                    </p>
                    <div className="mt-auto">
                      {intensity === "CUSTOM" ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={customValue || ""}
                            onChange={(e) =>
                              setCustomValue(
                                Number.parseInt(e.target.value) || 0
                              )
                            }
                            className="w-full text-sm h-8"
                            placeholder="Enter value"
                          />
                          <span className="text-sm text-gray-500">
                            {getUnitLabel()}
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm font-medium">
                          Total goal: Custom
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Step 3: Challenge Details */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold flex items-center gap-2 text-[#2b7fff]">
              <span>Step 3: Mission Details</span>
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Mission Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Mission Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (!userEditedDescription) {
                      setUserEditedDescription(true);
                    }
                  }}
                  className="w-full"
                  rows={3}
                />
                {!userEditedDescription && (
                  <p className="text-xs text-gray-500">
                    Description will update automatically until you edit it.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        <CardFooter className="flex justify-end gap-2 p-6 pt-0">
          <Link to="/missions">
            <Button variant="outline">Cancel</Button>
          </Link>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              className="bg-[#2b7fff] hover:bg-[#1a6eee]"
              disabled={
                createMissionMutation.isPending ||
                !name ||
                (intensity === "CUSTOM" && !customValue)
              }
              onClick={() => handleCreateMission()}
            >
              {createMissionMutation.isPending
                ? "Creating..."
                : "Create Mission"}
            </Button>
          </motion.div>
        </CardFooter>
      </motion.div>
    </div>
  );
}
